import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import AppImage from '../../../components/AppImage';

const CourtConfiguration = ({ courts, onStatusUpdate }) => {
  const [editingCourt, setEditingCourt] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { 
        label: 'Disponível', 
        className: 'bg-success/10 text-success border-success/20' 
      },
      occupied: { 
        label: 'Ocupada', 
        className: 'bg-warning/10 text-warning border-warning/20' 
      },
      maintenance: { 
        label: 'Manutenção', 
        className: 'bg-destructive/10 text-destructive border-destructive/20' 
      }
    };
    
    const config = statusConfig?.[status] || statusConfig?.available;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const handleStatusUpdate = (courtId, newStatus) => {
    onStatusUpdate?.(courtId, newStatus);
  };

  const CourtEditModal = ({ court, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      name: court?.name || '',
      type: court?.type || 'society',
      surface: court?.surface || '',
      capacity: court?.capacity || 0,
      hourlyRate: court?.hourlyRate || 0,
      lighting: court?.lighting || false,
      description: court?.description || '',
      amenities: court?.amenities || []
    });

    const handleInputChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleSave = () => {
      onSave?.(court?.id, formData);
      onClose();
    };

    if (!court) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground">
                Configurar Quadra
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Nome da Quadra
                  </label>
                  <Input
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    placeholder="Ex: Quadra 1 - Society"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Tipo de Quadra
                  </label>
                  <Select
                    value={formData?.type}
                    onChange={(value) => handleInputChange('type', value)}
                  >
                    <option value="society">Society</option>
                    <option value="futsal">Futsal</option>
                    <option value="beach_tennis">Beach Tennis</option>
                    <option value="volleyball">Vôlei</option>
                    <option value="basketball">Basquete</option>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Tipo de Piso
                  </label>
                  <Input
                    type="text"
                    value={formData?.surface}
                    onChange={(e) => handleInputChange('surface', e?.target?.value)}
                    placeholder="Ex: Grama Sintética"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Capacidade (jogadores)
                  </label>
                  <Input
                    type="number"
                    value={formData?.capacity}
                    onChange={(e) => handleInputChange('capacity', parseInt(e?.target?.value))}
                    min="1"
                    max="22"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    Valor por Hora (R$)
                  </label>
                  <Input
                    type="number"
                    value={formData?.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', parseFloat(e?.target?.value))}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="flex items-center">
                  <Checkbox
                    id="lighting"
                    checked={formData?.lighting}
                    onChange={(checked) => handleInputChange('lighting', checked)}
                  />
                  <label htmlFor="lighting" className="ml-2 text-sm text-card-foreground">
                    Iluminação noturna
                  </label>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Descreva as características da quadra..."
                />
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-3">
                  Comodidades
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    'Vestiário',
                    'Chuveiro',
                    'Estacionamento',
                    'Lanchonete',
                    'Equipamentos',
                    'Arquibancada'
                  ]?.map((amenity) => (
                    <div key={amenity} className="flex items-center">
                      <Checkbox
                        id={amenity}
                        checked={formData?.amenities?.includes(amenity)}
                        onChange={(checked) => {
                          if (checked) {
                            handleInputChange('amenities', [...(formData?.amenities || []), amenity]);
                          } else {
                            handleInputChange('amenities', formData?.amenities?.filter(a => a !== amenity));
                          }
                        }}
                      />
                      <label htmlFor={amenity} className="ml-2 text-sm text-card-foreground">
                        {amenity}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button variant="default" onClick={handleSave}>
                  Salvar Alterações
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Configuração de Quadras
          </h2>
          <p className="text-muted-foreground">
            Gerencie preços, disponibilidade e informações das quadras
          </p>
        </div>

        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          iconSize={16}
        >
          Nova Quadra
        </Button>
      </div>

      {/* Courts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courts?.map((court) => (
          <div key={court?.id} className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
            {/* Court Image */}
            <div className="relative h-48">
              <AppImage
                src={court?.image}
                alt={court?.imageAlt}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 right-3">
                {getStatusBadge(court?.status)}
              </div>
            </div>

            {/* Court Info */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading font-bold text-card-foreground">
                    {court?.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {court?.surface} • {court?.capacity} jogadores
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingCourt(court)}
                  iconName="Settings"
                  iconSize={16}
                />
              </div>

              {/* Pricing */}
              <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Valor por hora:</span>
                  <span className="text-lg font-heading font-bold text-primary">
                    R$ {court?.hourlyRate?.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Today's Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center p-2 bg-muted/30 rounded">
                  <div className="text-lg font-heading font-bold text-card-foreground">
                    {court?.bookingsToday}
                  </div>
                  <div className="text-xs text-muted-foreground">Reservas hoje</div>
                </div>
                <div className="text-center p-2 bg-muted/30 rounded">
                  <div className="text-lg font-heading font-bold text-card-foreground">
                    R$ {court?.revenueToday?.toFixed(0)}
                  </div>
                  <div className="text-xs text-muted-foreground">Receita hoje</div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {court?.lighting && (
                    <span className="px-2 py-1 text-xs bg-warning/10 text-warning rounded-full border border-warning/20">
                      Iluminação
                    </span>
                  )}
                  <span className="px-2 py-1 text-xs bg-info/10 text-info rounded-full border border-info/20">
                    {court?.type}
                  </span>
                </div>
              </div>

              {/* Next Maintenance */}
              {court?.nextMaintenance && (
                <div className="mb-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Tool" size={14} />
                    <span>
                      Próxima manutenção: {new Date(court?.nextMaintenance)?.toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              )}

              {/* Status Actions */}
              <div className="flex space-x-2">
                {court?.status !== 'available' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusUpdate(court?.id, 'available')}
                    iconName="Check"
                    iconPosition="left"
                    iconSize={14}
                    className="flex-1"
                  >
                    Disponível
                  </Button>
                )}
                
                {court?.status !== 'maintenance' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleStatusUpdate(court?.id, 'maintenance')}
                    iconName="Tool"
                    iconPosition="left"
                    iconSize={14}
                    className="flex-1"
                  >
                    Manutenção
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Court Configuration Modal */}
      <CourtEditModal
        court={editingCourt}
        onClose={() => setEditingCourt(null)}
        onSave={(courtId, data) => {
          // Handle save logic here
          console.log('Saving court:', courtId, data);
          setEditingCourt(null);
        }}
      />
    </div>
  );
};

export default CourtConfiguration;